// engines/audio-engine.js — pure state machine for audio playback.
//
// Direct JS port of `Assets/_App/Engines/AudioEngine.cs` from the Unity port.
// Owns the IAudioPlayer (BrowserAudioPlayer in core/audio-engine.js, injected
// at construction) and a LyricsEngine instance. Forwards player time updates
// into the LyricsEngine so the screen sees one unified stream of events.
//
// Architecture rule (verbatim from CLAUDE.md): pure state machine, no DOM
// imports. Player is injected; screens never reach into the player directly.
//
// State transitions:
//   Idle    ── load(item, file) ──▶ Ready
//   Ready   ── play()           ──▶ Playing
//   Playing ── pause()          ──▶ Paused
//   Paused  ── play()           ──▶ Playing
//   *       ── stop()           ──▶ Ready
//   *       ── unload()         ──▶ Idle
//   Playing ── (natural end)    ──▶ Ready  (also fires playbackEnded)

import { LyricsEngine } from "./lyrics-engine.js";

/** @typedef {import("../types").RMItem} RMItem */
/** @typedef {import("../types").LyricLine} LyricLine */
/** @typedef {"Idle"|"Ready"|"Playing"|"Paused"|"Error"} PlaybackState */

/**
 * @typedef {object} IAudioPlayer
 * @property {(filePath: string) => void} load
 * @property {(sources: Array<{url: string, type: string}>) => void} loadSources
 * @property {() => Promise<void>}        play
 * @property {() => void}                 pause
 * @property {() => void}                 stop
 * @property {(sec: number) => void}      seekTo
 * @property {() => void}                 unload
 * @property {number}                     currentTimeSec
 * @property {number}                     durationSec
 * @property {boolean}                    isPlaying
 * @property {(type: string, fn: EventListener) => void} addEventListener
 * @property {(type: string, fn: EventListener) => void} removeEventListener
 */

export class AudioEngine extends EventTarget {
  /**
   * @param {IAudioPlayer} player — injected platform player (BrowserAudioPlayer)
   */
  constructor(player) {
    super();
    /** @type {IAudioPlayer} */          this._player = player;
    /** @type {PlaybackState} */         this._state = "Idle";
    /** @type {RMItem | null} */         this._item = null;
    /** @type {string | null} */         this._lastError = null;

    this.lyricsEngine = new LyricsEngine();

    // Forward time updates into the LyricsEngine and out as our own events.
    this._onTime = /** @type {EventListener} */ ((e) => {
      const t = /** @type {CustomEvent<number>} */ (e).detail;
      this.lyricsEngine.getCurrentLineIndex(t);
      this.dispatchEvent(new CustomEvent("timeUpdated", { detail: t }));
    });
    this._onEnded = /** @type {EventListener} */ (() => {
      this._setState("Ready");
      this.dispatchEvent(new Event("playbackEnded"));
    });
    this._onError = /** @type {EventListener} */ ((e) => {
      this._lastError = String(/** @type {CustomEvent<string>} */ (e).detail || "audio error");
      this._setState("Error");
      this.dispatchEvent(new CustomEvent("error", { detail: this._lastError }));
    });

    player.addEventListener("timeUpdated", this._onTime);
    player.addEventListener("playbackEnded", this._onEnded);
    player.addEventListener("error", this._onError);

    // Surface lyric line changes as our own event for screen consumption.
    this.lyricsEngine.addEventListener("lineChanged", /** @type {EventListener} */ ((e) => {
      this.dispatchEvent(new CustomEvent("lyricLineChanged", { detail: /** @type {CustomEvent<number>} */ (e).detail }));
    }));
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Load an item for playback.
   *
   * `media` may be either a single resolved URL (string) or an ordered list
   * of `<source>` candidates (`Array<{url, type}>`). The list form lets the
   * browser pick a format it can decode — the player ships `.m4a` first
   * for iOS Safari / Chromium and `.ogg` as a Linux-Firefox fallback. See
   * `data/media-paths.js#resolveAudioSources`.
   *
   * @param {RMItem} item
   * @param {string | Array<{url: string, type: string}>} media
   * @param {LyricLine[]} [lyrics]  optional pre-loaded lyric lines
   */
  load(item, media, lyrics) {
    if (this._state === "Playing" || this._state === "Paused") this.stop();
    this._item = item;
    this.lyricsEngine.clear();
    if (lyrics && lyrics.length) this.lyricsEngine.loadLyrics(lyrics);
    if (typeof media === "string") this._player.load(media);
    else this._player.loadSources(media);
    this._lastError = null;
    this._setState("Ready");
  }

  /**
   * Set or replace lyrics for the currently-loaded item. Useful when lyrics
   * are lazy-loaded after the audio has been requested.
   * @param {LyricLine[]} lyrics
   */
  setLyrics(lyrics) {
    this.lyricsEngine.loadLyrics(lyrics);
  }

  // ── Transport ─────────────────────────────────────────────────────────────

  async play() {
    if (this._state === "Idle") return;
    try {
      await this._player.play();
      this._setState("Playing");
    } catch (err) {
      this._lastError = String(err);
      this._setState("Error");
      this.dispatchEvent(new CustomEvent("error", { detail: this._lastError }));
    }
  }

  pause() {
    if (this._state !== "Playing") return;
    this._player.pause();
    this._setState("Paused");
  }

  stop() {
    if (this._state === "Idle") return;
    this._player.stop();
    this._setState("Ready");
  }

  /** @param {number} sec */
  seekTo(sec) {
    this._player.seekTo(sec);
  }

  seekBack10s() {
    const t = Math.max(0, this._player.currentTimeSec - 10);
    this._player.seekTo(t);
  }

  unload() {
    this._player.unload();
    this.lyricsEngine.clear();
    this._item = null;
    this._setState("Idle");
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  /**
   * Detach from the player. Call when the screen owning this engine is
   * disconnected. Otherwise event listeners stack across screen mounts —
   * the same pitfall called out in CLAUDE.md "Event handler stacking".
   */
  dispose() {
    this._player.removeEventListener("timeUpdated", this._onTime);
    this._player.removeEventListener("playbackEnded", this._onEnded);
    this._player.removeEventListener("error", this._onError);
    this.unload();
  }

  // ── Snapshot ──────────────────────────────────────────────────────────────

  /**
   * Plain-object snapshot for screens to render. Mirrors the snapshot
   * convention from the Unity port; screens read this rather than reaching
   * into engine internals.
   * @returns {{
   *   state: PlaybackState,
   *   item: RMItem | null,
   *   currentTimeSec: number,
   *   durationSec: number,
   *   lastError: string | null,
   *   lyrics: ReturnType<LyricsEngine["snapshot"]>,
   * }}
   */
  snapshot() {
    return {
      state: this._state,
      item: this._item,
      currentTimeSec: this._player.currentTimeSec,
      durationSec: this._player.durationSec,
      lastError: this._lastError,
      lyrics: this.lyricsEngine.snapshot(),
    };
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  /** @param {PlaybackState} next */
  _setState(next) {
    if (this._state === next) return;
    this._state = next;
    this.dispatchEvent(new CustomEvent("stateChanged", { detail: next }));
  }
}
